import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

/**
 * Cumulative Pipeline Build Line Chart Component
 *
 * Displays a line chart showing Cumulative Qualified Pipeline build ($M) by month
 * Includes two lines:
 * - Total(cum) - Target cumulative (orange with square markers)
 * - Actual (cum) - Actual cumulative (blue with diamond markers)
 */
function CumulativePipelineBuildLineChart({ data, title }) {
  const chartRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Handle resize
  useEffect(() => {
    if (!chartRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const width = entry.contentRect.width;
        if (width > 0) {
          setContainerWidth(width);
        }
      }
    });

    resizeObserver.observe(chartRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!data || !chartRef.current || !Array.isArray(data) || data.length === 0 || containerWidth === 0) return;

    d3.select(chartRef.current).selectAll('*').remove();

    // Responsive sizing
    const isSmallScreen = containerWidth < 400;
    const isMediumScreen = containerWidth < 600;

    const margin = {
      top: isSmallScreen ? 40 : 50,
      right: isSmallScreen ? 15 : 30,
      bottom: isSmallScreen ? 50 : 60,
      left: isSmallScreen ? 50 : 70
    };
    const width = Math.max(200, containerWidth - margin.left - margin.right);
    const height = isSmallScreen ? 280 : 350;

    // Responsive font sizes
    const fontSize = {
      title: isSmallScreen ? 10 : (isMediumScreen ? 11 : 12),
      axis: isSmallScreen ? 8 : (isMediumScreen ? 9 : 10),
      label: isSmallScreen ? 7 : (isMediumScreen ? 8 : 9),
      legend: isSmallScreen ? 8 : (isMediumScreen ? 9 : 10)
    };

    const totalWidth = width + margin.left + margin.right;
    const totalHeight = height + margin.top + margin.bottom;

    const svg = d3
      .select(chartRef.current)
      .append('svg')
      .attr('viewBox', `0 0 ${totalWidth} ${totalHeight}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('width', '100%')
      .style('height', 'auto')
      .style('max-height', isSmallScreen ? '350px' : '450px')
      .style('display', 'block')
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Filter data: only include months with valid values
    const validTargetData = data.filter(d => d.targetCum !== undefined && d.targetCum !== null);
    const validActualData = data.filter(d => d.actualCum !== undefined && d.actualCum !== null);
    const hasGoalData = validTargetData.length > 0 && validTargetData.some(d => d.targetCum > 0);

    // X Scale - months
    const xScale = d3
      .scaleBand()
      .domain(data.map(d => d.month))
      .range([0, width])
      .padding(0.1);

    // Y Scale - cumulative values in millions
    const maxActual = d3.max(validActualData, d => d.actualCum || 0) || 0;
    const maxTarget = hasGoalData ? (d3.max(validTargetData, d => d.targetCum || 0) || 0) : 0;
    const maxValue = Math.max(maxActual, maxTarget) * 1.2 || 140;
    const yScale = d3
      .scaleLinear()
      .domain([0, maxValue])
      .range([height, 0]);

    // Grid lines
    svg
      .append('g')
      .attr('class', 'grid')
      .call(d3.axisLeft(yScale).ticks(7).tickSize(-width).tickFormat(''))
      .selectAll('line')
      .style('stroke', '#e0e0e0')
      .style('stroke-opacity', 0.7);

    svg.selectAll('.domain').remove();

    // X Axis
    svg
      .append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale).tickSize(0))
      .selectAll('text')
      .style('font-size', `${fontSize.axis}px`)
      .style('text-anchor', 'middle');

    // Y Axis
    svg
      .append('g')
      .call(d3.axisLeft(yScale).ticks(isSmallScreen ? 5 : 7).tickFormat(d => `${d}`))
      .selectAll('text')
      .style('font-size', `${fontSize.axis}px`);

    // Line generator for target (Total cum)
    const lineTarget = d3
      .line()
      .x(d => xScale(d.month) + xScale.bandwidth() / 2)
      .y(d => yScale(d.targetCum))
      .curve(d3.curveMonotoneX);

    // Line generator for actual
    const lineActual = d3
      .line()
      .x(d => xScale(d.month) + xScale.bandwidth() / 2)
      .y(d => yScale(d.actualCum))
      .curve(d3.curveMonotoneX);

    // Draw target line (orange) - only if goal data exists
    if (hasGoalData && validTargetData.length > 0) {
      svg
        .append('path')
        .datum(validTargetData)
        .attr('fill', 'none')
        .attr('stroke', '#FF9800') // Orange
        .attr('stroke-width', 2.5)
        .attr('d', lineTarget);

      // Draw target points (square markers)
      svg
        .selectAll('.point-target')
        .data(validTargetData)
        .enter()
        .append('rect')
        .attr('class', 'point-target')
        .attr('x', d => xScale(d.month) + xScale.bandwidth() / 2 - 4)
        .attr('y', d => yScale(d.targetCum) - 4)
        .attr('width', 8)
        .attr('height', 8)
        .attr('fill', '#FF9800')
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.5);

      // Value labels on target points
      svg
        .selectAll('.point-label-target')
        .data(validTargetData)
        .enter()
        .append('text')
        .attr('class', 'point-label-target')
        .attr('x', d => xScale(d.month) + xScale.bandwidth() / 2)
        .attr('y', d => {
          const targetY = yScale(d.targetCum);
          const actualY = yScale(d.actualCum || 0);
          const diff = Math.abs(targetY - actualY);

          // Position label to avoid overlap
          if (diff < 25) {
            return targetY - 15;
          } else {
            return targetY - 10;
          }
        })
        .attr('text-anchor', 'middle')
        .attr('font-size', `${fontSize.label}px`)
        .attr('fill', '#FF9800')
        .attr('font-weight', 'bold')
        .text(d => d.targetCumFormatted);
    }

    // Draw actual line (blue)
    if (validActualData.length > 0) {
      svg
        .append('path')
        .datum(validActualData)
        .attr('fill', 'none')
        .attr('stroke', '#2196F3') // Blue
        .attr('stroke-width', 2.5)
        .attr('d', lineActual);

      // Draw actual points (diamond markers)
      svg
        .selectAll('.point-actual')
        .data(validActualData)
        .enter()
        .append('path')
        .attr('class', 'point-actual')
        .attr('d', d3.symbol().type(d3.symbolDiamond).size(64))
        .attr('transform', d => `translate(${xScale(d.month) + xScale.bandwidth() / 2},${yScale(d.actualCum)})`)
        .attr('fill', '#2196F3')
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.5);

      // Value labels on actual points
      svg
        .selectAll('.point-label-actual')
        .data(validActualData)
        .enter()
        .append('text')
        .attr('class', 'point-label-actual')
        .attr('x', d => xScale(d.month) + xScale.bandwidth() / 2)
        .attr('y', d => {
          const actualY = yScale(d.actualCum);
          const targetY = yScale(d.targetCum || 0);
          const diff = Math.abs(actualY - targetY);

          // If Actual is below Target but they are very close (collision risk)
          // we move it slightly further up or keep it tight
          if (diff > 0 && diff < 20) {
            return actualY - 10; // Slightly more gap to avoid the square above it
          } else {
            return actualY - 7;  // Tightly above the diamond
          }
        })
        .attr('text-anchor', 'middle')
        .attr('font-size', `${fontSize.label}px`)
        .attr('fill', '#333')
        .attr('font-weight', 'bold')
        .text(d => d.actualCumFormatted);
    }

    // Title
    svg
      .append('text')
      .attr('x', width / 2)
      .attr('y', isSmallScreen ? -20 : -25)
      .attr('text-anchor', 'middle')
      .attr('font-size', `${fontSize.title}px`)
      .attr('font-weight', 'bold')
      .text(title || '');

    // Y axis label
    svg
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -height / 2)
      .attr('y', isSmallScreen ? -35 : -50)
      .attr('text-anchor', 'middle')
      .attr('font-size', `${fontSize.axis}px`)
      .text('ACV($M)');

    // Legend - only show goal entry if goal data exists
    const legendData = [];
    if (hasGoalData) {
      legendData.push({ label: '-Total(cum)', color: '#FF9800', marker: 'square' });
    }
    legendData.push({ label: '-Actual (cum)', color: '#2196F3', marker: 'diamond' });

    const legendSpacing = isSmallScreen ? 80 : 100;
    const totalLegendWidth = legendData.length * legendSpacing;
    const legend = svg
      .append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${width / 2 - totalLegendWidth / 2}, ${height + (isSmallScreen ? 28 : 35)})`);

    legendData.forEach((item, i) => {
      const legendItem = legend
        .append('g')
        .attr('transform', `translate(${i * legendSpacing}, 0)`);

      // Legend marker
      if (item.marker === 'square') {
        legendItem
          .append('rect')
          .attr('x', 0)
          .attr('y', -6)
          .attr('width', isSmallScreen ? 8 : 10)
          .attr('height', isSmallScreen ? 8 : 10)
          .attr('fill', item.color);
      } else {
        legendItem
          .append('path')
          .attr('d', d3.symbol().type(d3.symbolDiamond).size(isSmallScreen ? 48 : 64))
          .attr('transform', 'translate(5, 0)')
          .attr('fill', item.color);
      }

      // Legend text
      legendItem
        .append('text')
        .attr('x', isSmallScreen ? 14 : 18)
        .attr('y', 4)
        .attr('font-size', `${fontSize.legend}px`)
        .attr('fill', '#333')
        .text(item.label);
    });

  }, [data, title, containerWidth]);

  return (
    <div
      ref={chartRef}
      id="printChart"
      style={{
        width: '100%',
        minHeight: '300px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden'
      }}
    />
  );
}

export default CumulativePipelineBuildLineChart;
